import { useCallback } from 'react';
import { useAccess } from '@sitemax/smx-permission';
import { DownloadOutlined } from '@ant-design/icons';

import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isCategory, isDocument } from '../../../../utilities/constants';
import { Category, Document } from 'interfaces/documents';
import { downloadDocument, bulkDownload } from 'slices/documentsActions';
import { useModal } from 'hooks/useModal';
import { modalName as downloadsModal } from 'modals/DownloadsModal';

export const Download = (
    selected: (Category | Document)[],
) => {
    const dispatch = useAppDispatch();

    const { loading, breadcrumbs } = useAppSelector((state) => state.documents);
    const { openModal } = useModal();
    const isProjectDocument = breadcrumbs?.length > 0 ? breadcrumbs[breadcrumbs.length - 1].is_project_document : false;
    const canView = useAccess(isProjectDocument ? ['files.project.view'] : ['files.all.view']);

    const downloadItems = useCallback(async () => {
        if (!selected || selected.length === 0) return;

        const documents = selected.filter(item => isDocument(item)) as Document[];
        const categories = selected.filter(item => isCategory(item)) as Category[];

        if (documents.length === 1 && categories.length === 0) {
            await dispatch(downloadDocument(documents[0]));
        } else {
            const documentIds = documents.map(doc => String(doc.id));
            const categoryIds = categories.map(cat => String(cat.uuid));
            let data: any = {
                entities: [...documentIds, ...categoryIds],
            };

            if (categories.length === 1 && documents.length === 0) {
                data.filename = categories[0].name;
            }

            const response = await dispatch(bulkDownload(data)).unwrap();
            if (response && response.signature) {
                openModal(downloadsModal);
            }
        }
    }, [selected, dispatch]);

    const getLabel = () => {
        if (selected.length === 1) {
            return isDocument(selected[0]) ? 'Download' : 'Download Folder';
        }
        return 'Download as ZIP';
    };

    if (!canView) return null;

    return {
        key: 'Download',
        icon: <DownloadOutlined />,
        disabled: !selected.length || loading,
        label: loading ? 'Downloading...' : getLabel(),
        onClick: () => downloadItems(),
    };
};
